import { ProposalPage } from '@/components/proposal-page'
import { QueryErrorScreen } from '@/components/query-error-screen'
import { ErrorAlert } from '@/components/ui/error-alert'
import { PageLoader } from '@/components/ui/page-loader'
import {
  MissingRequiredDataError,
  useAccountQuery,
  useAccountRoomsQuery,
  useInitializeQuote,
  usePhasesQuery,
  useInitializeProposal,
  usePrimaryContactQuery,
  useQuoteDetailsQuery,
  useQuoteQuery,
  useSystemsQuery,
} from '@/lib/api'
import { trackProposalView } from '@/lib/api/proposal'
import { useIsAuthenticated } from '@/lib/msal-config'
import { proposalViewer } from '@/lib/store'
import { deriveErrorMessage, getDocumentLabel } from '@/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { memo, useEffect, useState } from 'react'
import { z } from 'zod'

const AuthenticatedProposalPage = memo(function AuthenticatedProposalPage() {
  const { data: quote, isPending: isLoadingQuote, error: quoteError, refetch: refetchQuote } = useQuoteQuery()
  const { isPending: isLoadingAccount, error: accountError, refetch: refetchAccount } = useAccountQuery()
  const { isLoading: isLoadingContact, error: contactError, refetch: refetchContact } = usePrimaryContactQuery()
  const { isPending: isLoadingSystems, error: systemsError, refetch: refetchSystems } = useSystemsQuery()
  const { isPending: isLoadingPhases, error: phasesError, refetch: refetchPhases } = usePhasesQuery()
  const { isPending: isLoadingRooms, error: roomsError, refetch: refetchRooms } = useAccountRoomsQuery()
  const {
    isPending: isLoadingQuoteDetails,
    error: quoteDetailsError,
    refetch: refetchQuoteDetails,
  } = useQuoteDetailsQuery()
  const { initialized, error: initializedError } = useInitializeQuote()

  const documentLabel = getDocumentLabel(quote?.dsy_type).toLowerCase()

  if (quoteError) {
    return <QueryErrorScreen key="quote" title="Error loading quote" error={quoteError} onRetry={refetchQuote} />
  }

  if (accountError) {
    return <QueryErrorScreen key="account" title="Error loading account" error={accountError} onRetry={refetchAccount} />
  }

  // Only repairable contact data blocks the page — plain fetch failures degrade
  // to a blank contact, as they did before the fix-missing-data flow existed.
  if (contactError instanceof MissingRequiredDataError) {
    return <QueryErrorScreen key="contact" title="Error loading contact" error={contactError} onRetry={refetchContact} />
  }

  if (systemsError) {
    return <QueryErrorScreen key="systems" title="Error loading systems" error={systemsError} onRetry={refetchSystems} />
  }

  if (phasesError) {
    return <QueryErrorScreen key="phases" title="Error loading phases" error={phasesError} onRetry={refetchPhases} />
  }

  if (roomsError) {
    return <QueryErrorScreen key="rooms" title="Error loading rooms" error={roomsError} onRetry={refetchRooms} />
  }

  if (quoteDetailsError) {
    return (
      <QueryErrorScreen key="quote-details" title="Error loading quote details" error={quoteDetailsError} onRetry={refetchQuoteDetails} />
    )
  }

  if (isLoadingQuote) {
    return <PageLoader message="Loading quote..." />
  }

  if (isLoadingAccount) {
    return <PageLoader message="Loading account..." />
  }

  if (isLoadingContact) {
    return <PageLoader message="Loading contact..." />
  }

  if (isLoadingSystems) {
    return <PageLoader message="Loading systems..." />
  }

  if (isLoadingPhases) {
    return <PageLoader message="Loading phases..." />
  }

  if (isLoadingRooms) {
    return <PageLoader message="Loading rooms..." />
  }

  if (isLoadingQuoteDetails) {
    return <PageLoader message="Loading items..." />
  }

  if (initializedError) {
    return <ErrorAlert title={`Error initializing ${documentLabel}`} message={deriveErrorMessage(initializedError)} />
  }

  if (!initialized) {
    return <PageLoader message={`Initializing ${documentLabel}...`} />
  }

  return <ProposalPage />
})

const UnauthenticatedProposalPage = memo(function UnauthenticatedProposalPage() {
  const { data: quote, refetch } = useQuoteQuery()
  const {
    isLoading: isLoadingProposal,
    isSuccess: isProposalReady,
    error: proposalError,
    refetch: refetchProposal,
  } = useInitializeProposal()

  const documentLabel = getDocumentLabel(quote?.dsy_type).toLowerCase()

  useEffect(() => {
    if (isProposalReady) trackProposalView()
  }, [isProposalReady])

  if (proposalError) {
    // Deliberate 4xx gate from proposal-api (quote draft/closed/not found) — the
    // backend-authored message is client-safe and retrying won't help. Bodyless or
    // proxy 4xxs have no backendMessage and fall through to the retryable branch.
    if (
      isAxiosError(proposalError) &&
      (proposalError.response?.status === 400 || proposalError.response?.status === 404) &&
      proposalError.backendMessage
    ) {
      return (
        <ErrorAlert title={`${getDocumentLabel(quote?.dsy_type)} unavailable`} message={proposalError.backendMessage} />
      )
    }

    return (
      <ErrorAlert
        title={`Unable to load ${documentLabel}`}
        message={`We're having trouble loading your ${documentLabel}. Please try again, or contact your sales representative if the issue persists.`}
        onRetry={() => {
          refetch()
          refetchProposal()
        }}
      />
    )
  }

  if (isLoadingProposal) {
    return <PageLoader message={`Loading ${documentLabel}...`} />
  }

  return <ProposalPage />
})

type ViewerState = { status: 'checking' } | { status: 'staff' | 'customer' } | { status: 'failed'; error: unknown }

const SignedInProposalRoute = memo(function SignedInProposalRoute() {
  const [viewer, setViewer] = useState<ViewerState>({ status: 'checking' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    proposalViewer()
      .then((status) => {
        if (!cancelled) setViewer({ status })
      })
      .catch((error) => {
        if (!cancelled) setViewer({ status: 'failed', error })
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  if (viewer.status === 'failed') {
    return (
      <ErrorAlert
        title="Error signing in"
        message={deriveErrorMessage(viewer.error)}
        onRetry={() => {
          setViewer({ status: 'checking' })
          setAttempt((n) => n + 1)
        }}
      />
    )
  }

  if (viewer.status === 'checking') {
    return <PageLoader message="Signing in..." />
  }

  return viewer.status === 'staff' ? <AuthenticatedProposalPage /> : <UnauthenticatedProposalPage />
})

const ProposalRoute = memo(function ProposalRoute() {
  const isAuthenticated = useIsAuthenticated()

  if (isAuthenticated) {
    return <SignedInProposalRoute />
  }

  return <UnauthenticatedProposalPage />
})

export const Route = createFileRoute('/proposal')({
  validateSearch: z.object({ id: z.string() }),
  component: ProposalRoute,
})
